import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { IQuestionnaireOverviewItem } from '@interfaces/interfaces';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { filter, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { InterpolationService, TextInterpolation } from 'src/app/services/interpolation/interpolation.service';
import { WorkflowItemDirective } from 'src/app/workflow/directives/workflow-item/workflow-item.directive';
import { select, Store } from '@ngrx/store';
import * as Reducers from '../../../../store/reducers';
import {isNil} from 'lodash';
import { HIDE_MISSINGVALUE } from '@app/constants';

@Component({
	selector: 'workflow-overview-item',
	templateUrl: './overview-item.component.html',
	styleUrls: ['./overview-item.component.scss'],
})
export class OverviewItemComponent implements OnChanges, OnDestroy {

	@Input()
	config: IQuestionnaireOverviewItem;

	@Input()
	finishedAndLocked: boolean;

	@Input()
	alignTextLeft: boolean;

	show$: Observable<boolean>;
	text$: Observable<string>;
	language: string;
	isMissingValue: boolean;

	private config$ = new BehaviorSubject<IQuestionnaireOverviewItem>(undefined);
	private value$: Observable<unknown>;

	private subscription = new Subscription();
	private interpolation: TextInterpolation;

	constructor(
		private workflowItemDirective: WorkflowItemDirective,
		private interpolationService: InterpolationService,
		private store: Store<never>,
	) {
		this.value$ = this.config$.pipe(
			filter(config => !!config?.key),
			tap(config => this.setTemplate(config)),
			switchMap(config => {
				const missingValueKey = `${config.key}_missingValue`;
				return (this.finishedAndLocked ? this.workflowItemDirective.propertyFull$(missingValueKey) : this.workflowItemDirective.property$(missingValueKey)).pipe(
					tap(data => this.isMissingValue = !isNil(data) || config.showOnlyMissingValue),
					map(() => this.isMissingValue ? missingValueKey : config.key),
				);
			}),
			switchMap(key => this.finishedAndLocked ? this.workflowItemDirective.propertyFull$(key) : this.workflowItemDirective.property$(key)),
			shareReplay({refCount: false, bufferSize: 1}),
		);

		this.show$ = this.value$.pipe(
			map(value => (!!value || value === 0) && value !== HIDE_MISSINGVALUE),
		);

		this.text$ = this.value$.pipe(
			map(value => this.formatValue(value))
		);

		this.subscription.add(this.store.pipe(
			select(Reducers.getSettings),
		).subscribe(state => {
			this.language = state.locale;
			this.config$.next(this.config);
		}));
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes.config) {
			this.config$.next(this.config);
		}
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	private setTemplate(config: IQuestionnaireOverviewItem) {
		if (config?.template) {
			this.interpolation = this.interpolationService.interpolation(config.template);
		} else if (config?.unit) {
			this.interpolation = this.interpolationService.interpolation(`{{value | number}} ${config.unit}`);
		} else {
			this.interpolation = this.interpolationService.interpolation(`{{value | number}}`);
		}
	}

	private formatValue(value: unknown): string {
		return this.interpolation?.interpolate({ value: value }) || '' + value;
	}
}
